import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faFileCsv, faCheckCircle, faExclamationTriangle,
  faSyncAlt, faInfoCircle, faUser, faCalendarAlt, faClock, faTag, faEdit, faTrash,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import Dexie from 'dexie';

// ============================================================
// UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }
  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// CACHE DEXIE (24h) + cache périmé
// ============================================================
const db = new Dexie('HistoriqueCongeCacheDB');
db.version(1).stores({
  history: 'key, data, timestamp'
});
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedHistory(key) {
  const entry = await db.history.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_DURATION) {
    return entry.data;
  }
  return null;
}
async function setCachedHistory(key, data) {
  await db.history.put({ key, data, timestamp: Date.now() });
}
async function getStaleHistory(key) {
  const entry = await db.history.get(key);
  return entry ? entry.data : null;
}

// ============================================================
// DÉDOUBLONNAGE DES REQUÊTES
// ============================================================
const pendingFetches = new Map();

const API_URL = `${API_CONFIG2.apiUrl}conge/api_historique_conge.php`;

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const HistoriqueConge = ({ userId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 10 });
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [dataSource, setDataSource] = useState('cache');

  // ============================================================
  // RÉCUPÉRATION DE L'HISTORIQUE (avec déduplication, annulation, cache)
  // ============================================================
  const fetchHistory = useCallback(async (page = 1, search = '', forceRefresh = false, retryCount = 0) => {
    // Annulation de la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = `page_${page}_search_${search}`;
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Vérifier si une requête identique est en cours
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (result && isMountedRef.current) {
          setHistory(result.data);
          setPagination(result.pagination);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Définir l'état de chargement
    if (forceRefresh) {
      if (isMountedRef.current) setRefreshing(true);
    } else {
      if (isMountedRef.current) setLoading(true);
    }

    const fetchPromise = (async () => {
      try {
        // 1. Cache frais (sauf forceRefresh)
        if (!forceRefresh) {
          const cached = await getCachedHistory(cacheKey);
          if (cached) {
            if (isMountedRef.current) {
              setHistory(cached.data);
              setPagination(cached.pagination);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        // 2. Requête réseau avec signal d'annulation
        const url = `${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`;
        const data = await fetchJSON(url, { signal: controller.signal });

        if (data.status === 'success') {
          const result = { data: data.data, pagination: data.pagination };
          if (isMountedRef.current) {
            setHistory(data.data);
            setPagination(data.pagination);
            setDataSource(forceRefresh ? 'live (forcé)' : 'live');
          }
          await setCachedHistory(cacheKey, result);
          if (forceRefresh && isMountedRef.current) {
            setToast({ show: true, message: 'Données actualisées', variant: 'success' });
            setTimeout(() => { if (isMountedRef.current) setToast({ show: false }); }, 3000);
          }
          if (isMountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
          return result;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        // Annulation volontaire → on réinitialise le chargement
        if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
          if (isMountedRef.current) {
            setLoading(false);
            setRefreshing(false);
          }
          return null;
        }

        // Gestion du 429 ou erreur réseau
        const is429 = err.message?.includes('429') || err.message?.includes('HTTP 429');
        if (is429) {
          // Tenter le cache périmé
          const stale = await getStaleHistory(cacheKey);
          if (stale && isMountedRef.current) {
            setHistory(stale.data);
            setPagination(stale.pagination);
            setDataSource('cache (périmé)');
            setToast({ show: true, message: '429 – Utilisation du cache périmé', variant: 'warning' });
            setTimeout(() => { if (isMountedRef.current) setToast({ show: false }); }, 5000);
            setLoading(false);
            setRefreshing(false);
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          if (isMountedRef.current) {
            setToast({ show: true, message: `429 – Nouvel essai dans ${waitTime/1000}s`, variant: 'warning' });
          }
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchHistory(page, search, forceRefresh, retryCount + 1);
        }

        // Erreur réseau ou autre : fallback cache périmé
        const stale = await getStaleHistory(cacheKey);
        if (stale && isMountedRef.current) {
          setHistory(stale.data);
          setPagination(stale.pagination);
          setDataSource('cache (périmé)');
          setToast({ show: true, message: `Erreur réseau – Utilisation du cache`, variant: 'warning' });
          setTimeout(() => { if (isMountedRef.current) setToast({ show: false }); }, 5000);
          setLoading(false);
          setRefreshing(false);
          return stale;
        }

        // Pas de cache → afficher l'erreur
        if (isMountedRef.current) {
          setToast({ show: true, message: err.message || 'Erreur de chargement', variant: 'danger' });
          setTimeout(() => { if (isMountedRef.current) setToast({ show: false }); }, 5000);
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [pagination.limit]);

  // ============================================================
  // RECHERCHE (debounce)
  // ============================================================
  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchHistory(1, val, false);
    }, 400);
  };

  // ============================================================
  // INITIALISATION
  // ============================================================
  useEffect(() => {
    isMountedRef.current = true;
    fetchHistory();
    return () => {
      isMountedRef.current = false;
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchHistory]);

  // ============================================================
  // EXPORT CSV
  // ============================================================
  const exportCSV = () => {
    if (!history.length) {
      setToast({ show: true, message: 'Aucune donnée à exporter.', variant: 'warning' });
      setTimeout(() => setToast({ show: false }), 3000);
      return;
    }
    const headers = ['ID', 'Congé ID', 'Action', 'Employé', 'Type', 'Début', 'Fin', 'Jours', 'Ancien statut', 'Nouveau statut', 'Utilisateur', 'Date'];
    const rows = history.map(h => {
      const oldStatut = h.old_data?.statut || '';
      const newStatut = h.new_data?.statut || '';
      return [
        h.id,
        h.conge_id,
        h.action,
        `${h.employe_prenom || ''} ${h.employe_nom || ''}`,
        h.conge_type || '',
        h.dateDebut || '',
        h.dateFin || '',
        h.nbJours || '',
        oldStatut,
        newStatut,
        h.user_id || '',
        h.date_action
      ];
    });
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `historique_conge_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ============================================================
  // MODALE DE DÉTAILS
  // ============================================================
  const openDetailModal = (record) => {
    setSelectedRecord(record);
    setShowDetailModal(true);
  };

  const getActionBadge = (action) => {
    const lower = action?.toLowerCase() || '';
    if (lower === 'create') return { label: 'Création', color: '#3b82f6' };
    if (lower === 'update') return { label: 'Modification', color: '#f59e0b' };
    if (lower === 'delete') return { label: 'Suppression', color: '#ef4444' };
    return { label: action || 'Inconnu', color: '#94a3b8' };
  };

  // ============================================================
  // RENDU AVEC STYLES MOBILE OPTIMISÉS
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .historique-page {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: clamp(0.75rem, 2vw, 2rem);
          color: var(--text-primary);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: 24px;
          padding: clamp(1rem, 2vw, 1.8rem);
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .monetra-btn {
          padding: 8px 16px;
          border-radius: 30px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.04);
          color: var(--text-primary);
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 500;
          cursor: pointer;
          font-size: clamp(0.7rem, 1vw, 0.9rem);
          white-space: nowrap;
          min-height: 44px;
          touch-action: manipulation;
        }
        .monetra-btn:hover:not(:disabled) {
          background: rgba(255,255,255,0.08);
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(0,0,0,0.2);
        }
        .monetra-btn:disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .monetra-btn.primary {
          background: var(--accent-blue);
          color: #fff;
          border: none;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
        }
        .monetra-btn.primary:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          box-shadow: 0 8px 28px rgba(59, 130, 246, 0.4);
        }
        .monetra-btn.p-2 {
          padding: 6px 10px;
          min-width: 36px;
          justify-content: center;
        }

        .search-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 30px;
          padding: 4px 4px 4px 16px;
          transition: all 0.3s ease;
          flex: 1;
          min-width: 180px;
        }
        .search-wrapper:focus-within {
          border-color: var(--accent-blue);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.08);
        }
        .search-wrapper input {
          background: transparent;
          border: none;
          color: var(--text-primary);
          outline: none;
          padding: 10px 0;
          flex: 1;
          font-size: clamp(0.8rem, 1.2vw, 1rem);
        }
        .search-wrapper input::placeholder {
          color: var(--text-muted);
        }

        .glass-table {
          width: 100%;
          border-collapse: collapse;
          font-size: clamp(0.7rem, 1vw, 0.85rem);
        }
        .glass-table th {
          padding: 12px 10px;
          text-align: left;
          font-size: clamp(0.6rem, 0.8vw, 0.75rem);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--accent-blue);
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }
        .glass-table td {
          padding: 10px 10px;
          border-bottom: 1px solid rgba(255,255,255,0.02);
          vertical-align: middle;
        }
        .glass-table tr {
          transition: 0.2s;
        }
        .glass-table tr:hover {
          background: rgba(255,255,255,0.03);
        }

        .action-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: clamp(0.55rem, 0.8vw, 0.7rem);
          font-weight: 600;
        }

        .badge-source {
          font-size: 0.7rem;
          padding: 4px 14px;
          border-radius: 40px;
          background: rgba(59, 130, 246, 0.12);
          color: var(--accent-blue);
          border: 1px solid rgba(59, 130, 246, 0.12);
          white-space: nowrap;
          font-weight: 500;
          backdrop-filter: blur(4px);
        }
        .badge-source.live {
          background: rgba(46, 204, 113, 0.12);
          color: #2ecc71;
          border-color: rgba(46, 204, 113, 0.12);
        }
        .badge-source.warning {
          background: rgba(245, 158, 11, 0.12);
          color: #f59e0b;
          border-color: rgba(245, 158, 11, 0.12);
        }

        .monetra-modal .modal-content {
          background: rgba(10, 20, 45, 0.92);
          backdrop-filter: blur(30px);
          -webkit-backdrop-filter: blur(30px);
          border: 1px solid rgba(59, 130, 246, 0.12);
          border-radius: 28px;
          color: var(--text-primary);
          box-shadow: 0 30px 80px rgba(0,0,0,0.8);
        }
        .monetra-modal .modal-header {
          border-bottom: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .modal-footer {
          border-top: 1px solid rgba(59, 130, 246, 0.06);
        }
        .monetra-modal .btn-close {
          filter: invert(1) brightness(200%);
          opacity: 0.5;
        }

        .toast-monetra {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          -webkit-backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .fw-bold { font-weight: 600; }
        .small { font-size: 0.85rem; }
        .m-0 { margin: 0; }
        .mb-4 { margin-bottom: 1.5rem; }
        .me-1 { margin-right: 0.25rem; }
        .me-2 { margin-right: 0.5rem; }
        .p-2 { padding: 0.5rem; }
        .p-3 { padding: 1rem; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 1rem; }
        .d-flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .text-end { text-align: right; }
        .text-center { text-align: center; }
        .overflow-hidden { overflow: hidden; }
        .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .border-top { border-top: 1px solid rgba(255,255,255,0.04); }
        .border-opacity-10 { opacity: 0.1; }
        .ms-auto { margin-left: auto; }

        /* ============================================================
           RESPONSIVE MOBILE
           ============================================================ */
        @media (max-width: 768px) {
          .historique-page { padding: 10px; }
          .glass-card { padding: 1rem; border-radius: 16px; }

          /* Header en colonne */
          .d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
            gap: 12px;
          }
          .d-flex.justify-content-between.align-items-center > div:first-child {
            text-align: center;
          }
          .d-flex.justify-content-between.align-items-center .d-flex.gap-2 {
            flex-wrap: wrap;
            justify-content: center;
          }
          .monetra-btn {
            font-size: 0.75rem;
            padding: 6px 12px;
            min-height: 38px;
            flex: 1 0 auto;
            justify-content: center;
          }

          /* Barre de recherche en pleine largeur */
          .glass-card.d-flex.justify-content-between.align-items-center {
            flex-direction: column;
            align-items: stretch !important;
          }
          .search-wrapper {
            min-width: unset;
            width: 100%;
            max-width: 100% !important;
          }
          .ms-auto {
            margin-left: 0 !important;
            text-align: center;
          }

          /* Tableau : texte plus petit */
          .glass-table th, .glass-table td {
            padding: 6px 8px;
            font-size: 0.7rem;
          }
          .glass-table th:last-child, .glass-table td:last-child {
            min-width: 80px;
          }
          .action-badge {
            font-size: 0.6rem;
            padding: 2px 8px;
          }

          /* Pagination */
          .border-top.d-flex {
            flex-direction: column;
            align-items: center !important;
            gap: 8px;
          }
          .monetra-btn.p-2 {
            padding: 4px 12px;
            min-width: 40px;
          }

          /* Modale */
          .monetra-modal .modal-dialog {
            margin: 0.5rem;
          }
          .monetra-modal .modal-content {
            border-radius: 20px;
          }
        }

        @media (max-width: 480px) {
          .monetra-btn {
            font-size: 0.7rem;
            padding: 4px 10px;
            min-height: 34px;
          }
          .glass-table th, .glass-table td {
            font-size: 0.6rem;
            padding: 4px 6px;
          }
        }
      `}</style>

      <div className="historique-page">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
          <div>
            <h1 className="display-6 fw-bold mb-1" style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)' }}>
              Historique des congés
            </h1>
            <p className="text-secondary m-0" style={{ fontSize: 'clamp(0.75rem, 1.2vw, 0.95rem)' }}>
              Consultez toutes les modifications apportées aux demandes de congé.
            </p>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <span className={`badge-source ${dataSource === 'live' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button className="monetra-btn" onClick={() => fetchHistory(pagination.currentPage, searchTerm, false)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} /> Actualiser
            </button>
            <button className="monetra-btn" onClick={() => fetchHistory(pagination.currentPage, searchTerm, true)} disabled={loading || refreshing}>
              <FontAwesomeIcon icon={faSyncAlt} spin={loading || refreshing} style={{ color: '#ef4444' }} /> Forcer
            </button>
            <button className="monetra-btn" onClick={exportCSV} disabled={history.length === 0}>
              <FontAwesomeIcon icon={faFileCsv} /> Exporter CSV
            </button>
          </div>
        </div>

        {/* Recherche */}
        <div className="glass-card mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="search-wrapper" style={{ minWidth: '260px', flex: 1, maxWidth: '100%' }}>
            <FontAwesomeIcon icon={faSearch} className="text-secondary" />
            <input
              type="text"
              placeholder="Rechercher (employé, action, type...)"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="text-secondary small">
            <span>{pagination.totalRecords} enregistrements</span>
          </div>
        </div>

        {/* Tableau avec overlay de rafraîchissement */}
        <div className="glass-card p-0 overflow-hidden" style={{ position: 'relative' }}>
          {refreshing && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(11, 20, 40, 0.5)',
              backdropFilter: 'blur(2px)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '24px',
            }}>
              <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="text-secondary mt-3" style={{ fontSize: '1rem' }}>Mise à jour en cours…</p>
            </div>
          )}

          <div className="table-responsive" style={{ opacity: refreshing ? 0.4 : 1, transition: 'opacity 0.25s' }}>
            <table className="glass-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Congé</th>
                  <th>Action</th>
                  <th>Employé</th>
                  <th>Type</th>
                  <th>Période</th>
                  <th>Statut (ancien → nouveau)</th>
                  <th>Utilisateur</th>
                  <th>Date</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && !refreshing ? (
                  <tr><td colSpan="10" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
                ) : history.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-5 text-secondary">Aucun historique trouvé.</td></tr>
                ) : (
                  history.map(item => {
                    const { label, color } = getActionBadge(item.action);
                    const oldStatut = item.old_data?.statut || '-';
                    const newStatut = item.new_data?.statut || '-';
                    return (
                      <tr key={item.id}>
                        <td><span className="fw-bold text-secondary">#{item.id}</span></td>
                        <td><span className="text-secondary">#{item.conge_id}</span></td>
                        <td>
                          <span className="action-badge" style={{ backgroundColor: color + '22', color: color }}>
                            {label}
                          </span>
                        </td>
                        <td>
                          <span className="text-secondary">
                            <FontAwesomeIcon icon={faUser} className="me-1" />
                            {item.employe_prenom && item.employe_nom ? `${item.employe_prenom} ${item.employe_nom}` : 'Inconnu'}
                          </span>
                        </td>
                        <td><span className="text-secondary">{item.conge_type || '-'}</span></td>
                        <td>
                          <span className="text-secondary">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                            {item.dateDebut || '-'} → {item.dateFin || '-'}
                            {item.nbJours ? ` (${item.nbJours}j)` : ''}
                          </span>
                        </td>
                        <td>
                          <span className="text-secondary">
                            {oldStatut} → {newStatut}
                          </span>
                        </td>
                        <td><span className="text-secondary">{item.user_id || 'Système'}</span></td>
                        <td><span className="text-secondary" style={{ fontSize: '0.85rem' }}>{new Date(item.date_action).toLocaleString()}</span></td>
                        <td className="text-end">
                          <button className="monetra-btn p-2" onClick={() => openDetailModal(item)}>
                            <FontAwesomeIcon icon={faInfoCircle} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-3 border-top border-secondary border-opacity-10 d-flex justify-content-between align-items-center flex-wrap">
            <span className="text-secondary small">Page {pagination.currentPage} sur {pagination.totalPages}</span>
            <div className="d-flex gap-2">
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage <= 1 || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage - 1, searchTerm)}
              >
                ‹
              </button>
              <button
                className="monetra-btn p-2"
                disabled={pagination.currentPage >= pagination.totalPages || loading || refreshing}
                onClick={() => fetchHistory(pagination.currentPage + 1, searchTerm)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modale de détails */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered className="monetra-modal" size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold">
            <FontAwesomeIcon icon={faInfoCircle} className="text-success me-2" />
            Détails de l'enregistrement #{selectedRecord?.id}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRecord && (
            <div className="row g-3">
              <div className="col-md-6">
                <div className="text-secondary small">ID</div>
                <div>#{selectedRecord.id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Congé ID</div>
                <div>#{selectedRecord.conge_id}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Action</div>
                <div>
                  <span className="action-badge" style={{ backgroundColor: getActionBadge(selectedRecord.action).color + '22', color: getActionBadge(selectedRecord.action).color }}>
                    {getActionBadge(selectedRecord.action).label}
                  </span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Employé</div>
                <div>{selectedRecord.employe_prenom && selectedRecord.employe_nom ? `${selectedRecord.employe_prenom} ${selectedRecord.employe_nom}` : 'Inconnu'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Type</div>
                <div>{selectedRecord.conge_type || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Période</div>
                <div>{selectedRecord.dateDebut || '-'} → {selectedRecord.dateFin || '-'} ({selectedRecord.nbJours || 0} jours)</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Ancien statut</div>
                <div>{selectedRecord.old_data?.statut || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Nouveau statut</div>
                <div>{selectedRecord.new_data?.statut || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Motif</div>
                <div>{selectedRecord.old_data?.motif || selectedRecord.new_data?.motif || '-'}</div>
              </div>
              <div className="col-md-6">
                <div className="text-secondary small">Utilisateur</div>
                <div>{selectedRecord.user_id || 'Système'}</div>
              </div>
              <div className="col-12">
                <div className="text-secondary small">Date de l'action</div>
                <div>{new Date(selectedRecord.date_action).toLocaleString()}</div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDetailModal(false)} className="monetra-btn outline-secondary">Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-monetra p-3 ${toast.variant}`} style={{ minWidth: 'clamp(200px, 30vw, 300px)' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span style={{ fontSize: 'clamp(0.8rem, 1vw, 0.95rem)' }}>{toast.message}</span>
              <button
                className="btn-close-custom ms-auto"
                onClick={() => { if (isMountedRef.current) setToast({ ...toast, show: false }); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', opacity: 0.5, fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HistoriqueConge;